import { useEffect, useState } from "react";
import { Phone, Trophy } from "lucide-react";
import { api, apiError, brl, dataBR } from "@/lib/api";
import { EmptyState, ErrorState, Loading, Surface } from "@/components/ds";
import { Button } from "@/components/ui/button";

const MEDAL = ["text-amber-300", "text-slate-200", "text-amber-600"];

/** Ranking de compradores: valor comprado x quantidade de compras (agregado no backend). */
export function CustomerRanking({ onOpen }) {
  const [criteria, setCriteria] = useState("value");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async (by) => {
    setError("");
    setData(null);
    try {
      const { data: res } = await api.get("/customers/ranking", { params: { by } });
      setData(res);
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => {
    load(criteria);
  }, [criteria]);

  const switcher = (
    <div className="flex flex-wrap gap-2" data-testid="ranking-criteria">
      {[
        { key: "value", label: "Valor comprado" },
        { key: "count", label: "Quantidade de compras" },
      ].map((opt) => (
        <Button
          key={opt.key}
          size="sm"
          variant={criteria === opt.key ? "default" : "outline"}
          onClick={() => setCriteria(opt.key)}
          className="rounded-full"
          data-testid={`ranking-by-${opt.key}`}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="customers-ranking">
      {switcher}
      {error ? (
        <ErrorState message={error} onRetry={() => load(criteria)} />
      ) : !data ? (
        <Loading label="Montando o ranking..." />
      ) : data.rows.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="Ainda não há compradores no ranking"
          description="Só entram clientes com pedido de pagamento confirmado e não cancelado."
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{data.definition}</p>
          <Surface className="overflow-x-auto p-0" data-testid="ranking-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="hidden px-4 py-3 text-right font-medium sm:table-cell">Compras</th>
                  <th className="hidden px-4 py-3 text-right font-medium sm:table-cell">Valor</th>
                  <th className="hidden px-4 py-3 font-medium sm:table-cell">Última</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => onOpen(r.id)}
                    data-testid={`ranking-row-${r.id}`}
                    className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-secondary/40"
                  >
                    <td className={`px-4 py-3 font-num ${MEDAL[r.position - 1] || ""}`}>
                      {r.position}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{r.name || "Sem nome"}</p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Phone className="h-3 w-3" /> {r.phone || r.phone_key}
                      </p>
                      <p className="mt-0.5 font-num text-xs sm:hidden">
                        {brl(r.sold_total)}
                        <span className="text-muted-foreground"> · {r.sold_count} compra(s)</span>
                      </p>
                    </td>
                    <td
                      className={`hidden px-4 py-3 text-right font-num text-xs sm:table-cell ${
                        criteria === "count" ? "font-semibold" : "text-muted-foreground"
                      }`}
                    >
                      {r.sold_count}
                    </td>
                    <td
                      className={`hidden whitespace-nowrap px-4 py-3 text-right font-num text-xs sm:table-cell ${
                        criteria === "value" ? "font-semibold" : "text-muted-foreground"
                      }`}
                    >
                      {brl(r.sold_total)}
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-3 text-xs text-muted-foreground sm:table-cell">
                      {r.last_order_at ? dataBR(r.last_order_at) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Surface>
        </>
      )}
    </div>
  );
}
